class ProductGridGallery extends HTMLElement {
  static instances = new Set();
  static resizeListening = false;
  static resizeRaf = null;

  constructor() {
    super();
    this.onScroll = this.onScroll.bind(this);
    this.onArrowClick = this.onArrowClick.bind(this);
  }

  connectedCallback() {
    // Mobile cards intentionally use one static product image for faster scrolling
    // and to avoid creating a scroll/resize workload for every product card.
    if (window.matchMedia('(max-width: 768px)').matches) return;

    this.gallery = this.querySelector('.grid-product__gallery');
    this.arrows = this.querySelectorAll('.grid-product__arrow');
    this.indicator = this.querySelector('.grid-product__progress-indicator');

    if (!this.gallery) return;

    ProductGridGallery.instances.add(this);
    ProductGridGallery.bindSharedResize();

    this.updateProgress();
    this.gallery.addEventListener('scroll', this.onScroll, { passive: true });
    this.arrows.forEach((arrow) => {
      arrow.addEventListener('click', this.onArrowClick);
    });
  }

  disconnectedCallback() {
    ProductGridGallery.instances.delete(this);
    if (this.gallery) {
      this.gallery.removeEventListener('scroll', this.onScroll);
    }
    if (this.arrows) {
      this.arrows.forEach((arrow) => {
        arrow.removeEventListener('click', this.onArrowClick);
      });
    }
  }

  static bindSharedResize() {
    if (ProductGridGallery.resizeListening) return;
    ProductGridGallery.resizeListening = true;

    window.addEventListener('resize', () => {
      if (ProductGridGallery.resizeRaf) return;
      ProductGridGallery.resizeRaf = window.requestAnimationFrame(() => {
        ProductGridGallery.resizeRaf = null;
        ProductGridGallery.instances.forEach((instance) => instance.updateProgress());
      });
    }, { passive: true });
  }

  onScroll() {
    if (this.scrollRaf) return;
    this.scrollRaf = window.requestAnimationFrame(() => {
      this.scrollRaf = null;
      this.updateProgress();
    });
  }

  onArrowClick(e) {
    e.preventDefault();
    e.stopPropagation();

    const item = this.gallery.querySelector('.grid-product__gallery-item');
    if (!item) return;

    const itemWidth = item.offsetWidth;
    const direction = e.currentTarget.classList.contains('grid-product__arrow--next') ? 1 : -1;
    this.gallery.scrollBy({ left: itemWidth * direction, behavior: 'smooth' });
  }

  updateProgress() {
    if (!this.indicator || !this.gallery) return;

    const itemsCount = this.gallery.querySelectorAll('.grid-product__gallery-item').length;
    if (itemsCount <= 1) {
      this.indicator.style.width = '100%';
      this.indicator.style.marginLeft = '0';
      return;
    }

    const indicatorWidth = 100 / itemsCount;
    this.indicator.style.width = `${indicatorWidth}%`;

    const maxScroll = this.gallery.scrollWidth - this.gallery.clientWidth;
    if (maxScroll <= 0) {
      this.indicator.style.marginLeft = '0';
      return;
    }

    const currentScroll = Math.max(0, Math.min(this.gallery.scrollLeft, maxScroll));
    const currentTranslate = (currentScroll / maxScroll) * (100 - indicatorWidth);
    this.indicator.style.marginLeft = `${currentTranslate}%`;
  }
}

if (!customElements.get('product-grid-gallery')) {
  customElements.define('product-grid-gallery', ProductGridGallery);
}
